import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';
import { listFrom } from '../unwrap';

/**
 * A business's CRM / booking system.
 *
 * Secrets are write-only: the API returns hasClientSecret / hasRefreshToken
 * rather than the values, so the form shows "set" and sends a new value only
 * when one is typed. Sending them back blank would wipe a working credential —
 * the server treats blank as "unchanged" for exactly that reason.
 */
const BASE = '/v1/crm/providers';
const BUILTIN = '/v1/crm/builtin';
// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export type CrmProviderType =
  'ZOHO_CRM' | 'ZOHO_BOOKINGS' | 'ZENOTI' | 'LEADSQUARED' | 'CATCHMYCALL' | 'SHOPIFY' | 'N8N'
  /** Any HTTP API, described by configJson (CustomApiConfig.java). Bound per agent. */
  | 'CUSTOM_API';
export type CrmAuthType =
  | 'NONE' | 'API_KEY' | 'BEARER' | 'BASIC'
  | 'OAUTH2_CLIENT_CREDENTIALS' | 'OAUTH2_REFRESH_TOKEN' | 'ACCESS_SECRET'
  /** CUSTOM_API only: the secret (refreshToken) signs each request; configJson.hmac says how. */
  | 'HMAC_SHA256';

export interface CrmProvider {
  id?: string;
  name?: string;
  providerType: CrmProviderType;
  authType?: CrmAuthType;
  /** Where TOKENS come from. Region-specific for Zoho — accounts.zoho.in for India. */
  accountsServer?: string;
  /** Where API CALLS go. Zoho fills this in itself at first Test Connection. */
  apiDomain?: string;
  /** OAuth client id, or the LeadSquared accessKey. */
  clientId?: string;
  clientSecret?: string;
  /** Zoho refresh token, Zenoti API key, or LeadSquared secretKey. */
  refreshToken?: string;
  configJson?: string;
  supportsBooking?: boolean;
  supportsLeads?: boolean;
  enabled?: boolean;
  hasClientSecret?: boolean;
  hasRefreshToken?: boolean;
  lastTestedAt?: string;
  lastTestOk?: boolean;
  lastTestMessage?: string;
  /** Which provider wins when two are enabled: CrmResolver takes the oldest. */
  createdAt?: string;
}

export interface CrmTestResult { ok: boolean; message: string; apiDomain?: string }

export interface BuiltinService {
  id?: string;
  name: string;
  /** Everything depends on this: a 30-min haircut and a 4-hour keratin need different slots. */
  durationMinutes: number;
  /** How many can run at once — chairs, rooms, staff. 1 unless set. */
  concurrentCapacity?: number;
  price?: string;
  staffId?: string;
  enabled?: boolean;
}

export interface BuiltinHours {
  id?: string;
  dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  opensAt: string;
  closesAt: string;
}

export interface BuiltinAppointment {
  reference: string;
  serviceName?: string;
  startsAt: string;
  endsAt: string;
  customerName?: string;
  customerPhone: string;
  status: string;
}

export const crmProvidersService = {
  /**
   * @param organizationId whose integrations — omitted, the current organisation (the
   *   X-Organization-Id header). The server reads the query parameter FIRST and
   *   authorises it, so an agent's own organisation can be named explicitly.
   */
  list: async (organizationId?: string): Promise<ApiResponse<CrmProvider[]>> => {
    try {
      const res = await api.get(BASE, { params: organizationId ? { organizationId } : {} });
      return { success: true, data: listFrom(res.data, 'providers') };
    } catch (e: any) { return err(e, 'Failed to load integrations'); }
  },
  save: async (body: CrmProvider): Promise<ApiResponse<CrmProvider>> => {
    try { return (await api.post(BASE, body)).data; } catch (e: any) { return err(e, 'Failed to save integration'); }
  },
  remove: async (id: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BASE}/${id}`)).data; } catch (e: any) { return err(e, 'Failed to remove integration'); }
  },
  /** Returns 200 with ok:false when the connection fails — the reason is the point. */
  test: async (id: string): Promise<ApiResponse<CrmTestResult>> => {
    try { return (await api.post(`${BASE}/${id}/test`)).data; } catch (e: any) { return err(e, 'Connection test failed'); }
  },
};

/**
 * One Custom API push, as /v1/crm/custom-api/deliveries returns it
 * (CustomApiDeliveryController.masked). No request or response body is ever
 * returned — they carry the caller's details — and `caller` arrives already
 * masked to its last four digits. There is no way to unmask it, by design.
 */
export type CustomApiDeliveryStatus =
  'AWAITING_CALLER' | 'PENDING' | 'SENDING' | 'SENT' | 'REJECTED' | 'UNCONFIRMED' | 'INVALID' | 'FAILED';

export interface CustomApiDelivery {
  id: number;
  organizationId: string;
  providerId: string;
  agentId?: number | null;
  callId?: string | null;
  origin: 'IN_CALL' | 'POST_CALL';
  status: CustomApiDeliveryStatus;
  success?: boolean | null;
  reference?: string | null;
  errorMessages?: string | null;
  httpStatus?: number | null;
  attempts: number;
  lastError?: string | null;
  /** "••••1234" — masked server-side. */
  caller?: string | null;
  /** The binding's fixed values ({{agent.*}}) the push was rendered with. */
  agentValues?: Record<string, unknown> | null;
  createdAt?: string;
  sentAt?: string | null;
}

export interface CustomApiDeliveryPage {
  from: string;
  to: string;
  total: number;
  page: number;
  deliveries: CustomApiDelivery[];
}

export interface CustomApiDeliveryQuery {
  organizationId?: string;
  status?: CustomApiDeliveryStatus | '';
  /** yyyy-mm-dd, inclusive. The server defaults to 7 days before `to`. */
  from?: string;
  /** yyyy-mm-dd, inclusive. The server defaults to today. */
  to?: string;
  page?: number;
  /** 1..200, default 50. */
  size?: number;
}

/** One `custom_api_push` binding of an agent, as the check endpoint resolves it. */
export interface CustomApiBindingCheck {
  providerId: string;
  values: Record<string, unknown>;
  usable: boolean;
  /** Present when usable: the URL with placeholders shown as "x". */
  url?: string;
  placeholders?: string[];
  /** Present when not usable: why. */
  problem?: string;
}

export const customApiService = {
  /** Platform + provider admins only (MASTER/PLATFORM/SUPER/SERVICE_ADMIN, PROVIDER_OWNER/ADMIN). */
  deliveries: async (q: CustomApiDeliveryQuery): Promise<ApiResponse<CustomApiDeliveryPage>> => {
    try {
      const params: Record<string, string | number> = {};
      if (q.organizationId) params.organizationId = q.organizationId;
      if (q.status) params.status = q.status;
      if (q.from) params.from = q.from;
      if (q.to) params.to = q.to;
      if (q.page != null) params.page = q.page;
      if (q.size != null) params.size = q.size;
      const res = (await api.get('/v1/crm/custom-api/deliveries', { params })).data;
      const d = res?.data ?? {};
      return {
        success: true,
        data: {
          from: d.from, to: d.to, total: Number(d.total) || 0, page: Number(d.page) || 0,
          deliveries: Array.isArray(d.deliveries) ? d.deliveries : [],
        },
      };
    } catch (e: any) { return err(e, 'Failed to load Custom API deliveries'); }
  },
  /** MASTER_ADMIN / PLATFORM_ADMIN only. Each binding: usable, or why not. Sends nothing. */
  check: async (agentId: number, organizationId: string):
      Promise<ApiResponse<{ agentId: number; bindings: CustomApiBindingCheck[] }>> => {
    try {
      const res = (await api.get(`/v1/crm/custom-api/agents/${agentId}/check`, { params: { organizationId } })).data;
      const d = res?.data ?? {};
      return { success: true, data: { agentId: d.agentId, bindings: Array.isArray(d.bindings) ? d.bindings : [] } };
    } catch (e: any) { return err(e, 'Check failed'); }
  },
};

export const builtinBookingService = {
  services: async (): Promise<ApiResponse<BuiltinService[]>> => {
    try {
      const res = await api.get(`${BUILTIN}/services`);
      return { success: true, data: listFrom(res.data, 'services') };
    } catch (e: any) { return err(e, 'Failed to load services'); }
  },
  saveService: async (body: BuiltinService): Promise<ApiResponse<BuiltinService>> => {
    try { return (await api.post(`${BUILTIN}/services`, body)).data; } catch (e: any) { return err(e, 'Failed to save service'); }
  },
  removeService: async (id: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BUILTIN}/services/${id}`)).data; } catch (e: any) { return err(e, 'Failed to remove service'); }
  },
  hours: async (): Promise<ApiResponse<BuiltinHours[]>> => {
    try {
      const res = await api.get(`${BUILTIN}/hours`);
      return { success: true, data: listFrom(res.data, 'hours') };
    } catch (e: any) { return err(e, 'Failed to load opening hours'); }
  },
  saveHours: async (body: BuiltinHours): Promise<ApiResponse<BuiltinHours>> => {
    try { return (await api.post(`${BUILTIN}/hours`, body)).data; } catch (e: any) { return err(e, 'Failed to save hours'); }
  },
  /** Removing a day is how a business says "we're closed on Mondays" — every window on it. */
  closeDay: async (day: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BUILTIN}/hours/${day}`)).data; } catch (e: any) { return err(e, 'Failed to close that day'); }
  },
  /** Removes ONE opening window, for a day that keeps its morning but drops the evening. */
  removeHoursWindow: async (id: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BUILTIN}/hours/window/${id}`)).data; } catch (e: any) { return err(e, 'Failed to remove that time'); }
  },
  appointments: async (date?: string): Promise<ApiResponse<BuiltinAppointment[]>> => {
    try {
      const res = await api.get(`${BUILTIN}/appointments`, { params: date ? { date } : {} });
      return { success: true, data: listFrom(res.data, 'appointments') };
    } catch (e: any) { return err(e, 'Failed to load the diary'); }
  },
};

/** One finished call and what cpass did with it afterwards. */
export interface AgentCallEvent {
  id: string;
  agentId: number;
  callSid?: string;
  callFrom?: string;
  callTo?: string;
  callType?: string;
  durationSeconds?: number;
  status: 'RECEIVED' | 'PROCESSED' | 'PARTIAL' | 'FAILED' | 'NO_ACTIONS';
  attempts: number;
  lastError?: string;
  receivedAt?: string;
  processedAt?: string;
  /** tool name -> what happened, e.g. { capture_lead: "lead 5540… in ZOHO_CRM" } */
  actions?: Record<string, string>;
  /** The caller's first line — enough to recognise the call, never the transcript. */
  summary?: string;
}

export const agentCallsService = {
  list: async (agentId?: number, limit = 50):
      Promise<ApiResponse<{ calls: AgentCallEvent[]; counts: Record<string, number> }>> => {
    try {
      const p = new URLSearchParams();
      if (agentId) p.set('agentId', String(agentId));
      p.set('limit', String(limit));
      return (await api.get(`/v1/agent-calls?${p.toString()}`)).data;
    } catch (e: any) { return err(e, 'Failed to load calls'); }
  },

  /**
   * One call's conversation, fetched only when somebody opens it.
   *
   * Deliberately not part of `list`: that endpoint digests the payload to a
   * single line because it answers "did the pipeline work", and pulling every
   * transcript to render a table would put a page of private conversations on
   * the wire to answer a question about plumbing. The server scopes the lookup
   * by organisation AND call id together, so another tenant's id is a 404
   * rather than a refusal that confirms the call exists.
   *
   * `turns` is already parsed server-side — the runtime writes the list under
   * one of several key names and the server finds it by shape, so the browser
   * never has to know which provider wrote it.
   */
  transcript: async (callSid: string): Promise<ApiResponse<CallTranscript>> => {
    try {
      return (await api.get(`/v1/agent-calls/${encodeURIComponent(callSid)}/transcript`)).data;
    } catch (e: any) { return err(e, 'Failed to load the conversation'); }
  },
};

/** One call's conversation, as /v1/agent-calls/{callSid}/transcript returns it. */
export interface CallTranscript {
  callSid: string;
  durationSeconds?: number;
  recordingUrl?: string | null;
  /** Role/content turns. Empty is an ordinary answer: plenty of calls have none. */
  turns: Array<Record<string, unknown>>;
}
